// ═══════════════════════════════════════════════════════════════
// FUNCTION HOMES — which edge function answers which address
// ═══════════════════════════════════════════════════════════════
// MOS's backend used to be ONE Supabase edge function, make-server-e3a97ae5.
// Supabase refuses a function whose uploaded source passes 5 MB, and on
// 2026-10-23 the squeezed deploy copy stood at 4.31 MB with room for about
// four more features. So the backend is now more than one function, and
// THIS file is the one rule for which function serves which path:
//
//   /money/…            mos-money   the Books board, Jerry, the Bill Clerk,
//   /qbo/…              mos-money   the Controller, the reconciler, deposits,
//   /ar/…               mos-money   checks, loans, PayPal, Amazon, Plaid,
//   /monday/bid-sales/… mos-money   the AR board, the QuickBooks copy
//   everything else     make-server-e3a97ae5
//
// Three readers ask it, and none may keep its own table:
//   - the browser (src/utils/apiRoutes.ts): every fetch to a
//     make-server-e3a97ae5 address whose path lives elsewhere is rewritten
//     before it leaves the browser, so the 268 `API_BASE` lines in src/
//     never change and a /jobs call from a Books screen still reaches the
//     main function — the PATH decides, never the screen;
//   - the main function (index.ts, forwardToHome): every old address keeps
//     answering, forwarded to its new home, so a tab open on an old bundle,
//     a cron job not yet repointed, and the addresses registered with
//     Intuit, Amazon and Plaid never see a 404;
//   - the gate (scripts/function-homes-goldens.mjs, `npm run
//     test:function-homes`): every route a split function registers starts
//     with a prefix that sends it there, and no route left in the main
//     function uses one.
//
// Moving the NEXT family is: add its prefixes here, move its files, register
// them in that function's index.ts. Nothing else in the browser changes.
//
// No imports: the browser bundles this file too (the plan-target-version.ts
// pattern), so it must stay dependency-free.
// ═══════════════════════════════════════════════════════════════

/** The function every path lives in unless FUNCTION_HOMES says otherwise. */
export const MAIN_FUNCTION = "make-server-e3a97ae5";

/** The split functions the deploy ships beside the main one (plant-inventory
 *  and estimator are reached by their own names and never through a
 *  make-server address, so they are not "homes" of main-function paths). */
export const SPLIT_FUNCTIONS: readonly string[] = ["mos-money"];

/** A path prefix (no trailing slash) and the function that answers it. A
 *  longer prefix wins over a shorter one. */
export const FUNCTION_HOMES: ReadonlyArray<{ prefix: string; fn: string }> = [
  { prefix: "/money", fn: "mos-money" },
  { prefix: "/qbo", fn: "mos-money" },
  { prefix: "/ar", fn: "mos-money" },
  // co-billing-routes.tsx registers the Monday Bid Sales sync beside the
  // change-order billing check it feeds, so that one Monday path moved too.
  { prefix: "/monday/bid-sales", fn: "mos-money" },
];

/** The route path with its query string and hash cut off. */
function pathOnly(path: string): string {
  const q = path.search(/[?#]/);
  return q === -1 ? path : path.slice(0, q);
}

/** True when `path` is `prefix` itself or sits under it (`/ar` matches `/ar`
 *  and `/ar/jobs`, never `/area-definitions`). */
export function underPrefix(path: string, prefix: string): boolean {
  const p = pathOnly(path);
  return p === prefix || p.startsWith(prefix + "/");
}

/** Which function answers a route path such as `/money/bank/tie-out`. The
 *  path is what follows the function segment of the URL; it starts with `/`. */
export function functionFor(path: string): string {
  let best: { prefix: string; fn: string } | null = null;
  for (const home of FUNCTION_HOMES) {
    if (underPrefix(path, home.prefix) && (!best || home.prefix.length > best.prefix.length)) best = home;
  }
  return best ? best.fn : MAIN_FUNCTION;
}

/** The prefixes a function was given, for the main function's forwarder and
 *  for the gate. The main function owns no prefix: it is the default. */
export function prefixesOf(fn: string): string[] {
  return FUNCTION_HOMES.filter((h) => h.fn === fn).map((h) => h.prefix);
}

/** `/functions/v1/<function><path>` — the part of a Supabase URL after the
 *  project's origin, for a server-side caller that builds its own URL. */
export function functionPath(path: string): string {
  return `/functions/v1/${functionFor(path)}${path}`;
}
